<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { ApiError, login as loginRequest } from '@/api/auth.js';
import Banner from '@/components/ds/Banner.vue';
import Button from '@/components/ds/Button.vue';
import Field from '@/components/ds/Field.vue';
import Icon from '@/components/ds/Icon.vue';
import Input from '@/components/ds/Input.vue';
import PasswordField from '@/components/ds/PasswordField.vue';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';
import { safeReturnTo } from '@/utils/returnTo.js';

defineOptions({ name: 'LoginView' });

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const tenant = useTenantStore();
const { isAuthenticated } = storeToRefs(auth);

const email = ref('');
const password = ref('');
const submitting = ref(false);
const banner = ref(null);
const fieldError = ref({ email: null, password: null });
const retrySeconds = ref(0);
let retryTimer = null;

const returnToFromQuery = computed(() => safeReturnTo(route.query.return_to ?? null, null));

function clearLocalErrors() {
  fieldError.value = { email: null, password: null };
  banner.value = null;
}

function startRetryCountdown(seconds) {
  retrySeconds.value = seconds;
  if (retryTimer) clearInterval(retryTimer);
  retryTimer = setInterval(() => {
    if (retrySeconds.value <= 1) {
      retrySeconds.value = 0;
      clearInterval(retryTimer);
      retryTimer = null;
    } else {
      retrySeconds.value -= 1;
    }
  }, 1000);
}

async function onSubmit() {
  clearLocalErrors();

  if (!email.value) fieldError.value.email = 'Required';
  if (!password.value) fieldError.value.password = 'Required';
  if (fieldError.value.email || fieldError.value.password) return;

  submitting.value = true;
  try {
    const resp = await loginRequest({
      email: email.value.trim(),
      password: password.value,
      returnTo: returnToFromQuery.value
    });
    auth.setUser({
      email: email.value.trim(),
      role: resp.role,
      tenantScope: resp.scope ?? {}
    });
    const initialTenant = (resp.scope?.tenants ?? [])[0]?.app_id ?? null;
    if (initialTenant) tenant.init(initialTenant);

    const dest = safeReturnTo(resp.return_to ?? returnToFromQuery.value ?? null, '/');
    router.push(dest);
  } catch (err) {
    if (!(err instanceof ApiError)) {
      banner.value = { kind: 'network', message: "We couldn't reach the dashboard." };
      return;
    }
    if (err.status === 401) {
      banner.value = { kind: 'auth', message: 'Invalid email or password.' };
    } else if (err.status === 429) {
      const seconds = Number(err.retryAfter) || 60;
      startRetryCountdown(seconds);
      banner.value = { kind: 'rate', message: 'Too many attempts.' };
    } else if (err.status === 400 && Array.isArray(err.message)) {
      banner.value = { kind: 'validation', message: 'Please check the form and try again.' };
    } else {
      banner.value = { kind: 'unknown', message: 'Login failed. Please try again.' };
    }
  } finally {
    submitting.value = false;
  }
}

const rateMessage = computed(() => {
  if (banner.value?.kind !== 'rate' || retrySeconds.value <= 0) return null;
  return `Too many attempts. Try again in ${retrySeconds.value} s.`;
});

const bannerMessage = computed(() => {
  if (!banner.value) return null;
  if (banner.value.kind === 'rate') return rateMessage.value;
  return banner.value.message;
});

const disabled = computed(() => submitting.value || retrySeconds.value > 0);

onMounted(() => {
  if (isAuthenticated.value) router.replace(safeReturnTo(returnToFromQuery.value ?? null, '/'));
});

onBeforeUnmount(() => {
  if (retryTimer) clearInterval(retryTimer);
});
</script>

<template>
  <div class="signin signin--split">
    <div class="signin__pane-left">
      <div class="signin__brand">
        <span class="signin__brand-mark">O</span>
        <span class="signin__brand-name">Otera</span>
      </div>

      <div class="login__hero">
        <p class="signin__hero-title">
          Real-time operational<br> visibility.
        </p>
        <p class="signin__hero-lede">
          Ingest, latency and errors across all your apps in one console.
          Built to scan, not to navigate.
        </p>
      </div>
    </div>

    <div class="signin__pane-right">
      <div class="signin__card">
        <h1 class="signin__title">
          Sign in
        </h1>
        <p class="signin__sub">
          Continue with your account
        </p>

        <Banner
          v-if="bannerMessage"
          tone="danger"
          class="login__banner"
        >
          {{ bannerMessage }}
        </Banner>

        <form
          class="signin__form"
          novalidate
          @submit.prevent="onSubmit"
        >
          <Field
            label="Email"
            html-for="login-email"
            required
            :error="fieldError.email"
          >
            <Input
              id="login-email"
              v-model="email"
              type="email"
              size="lg"
              autocomplete="email"
              :error="!!fieldError.email"
              :disabled="disabled || undefined"
            >
              <template #prefix>
                <Icon
                  name="mail"
                  :size="14"
                />
              </template>
            </Input>
          </Field>

          <PasswordField
            id="login-password"
            v-model="password"
            label="Password"
            autocomplete="current-password"
            required
            :disabled="disabled"
            :error="fieldError.password"
          />

          <RouterLink
            v-if="auth.smtpEnabled"
            to="/password-reset/request"
            class="signin__forgot"
            data-test="forgot-password-link"
          >
            Forgot password?
          </RouterLink>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            full-width
            :loading="submitting"
            :disabled="disabled"
          >
            Sign in
          </Button>
        </form>

        <div class="signin__footer">
          <span>observability-component</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login__hero {
  margin-top: auto;
  margin-bottom: auto;
}

.login__banner {
  margin-bottom: var(--space-4);
}

/* Keep the desktop split layout within the viewport on phones/tablets. */
.signin__pane-left,
.signin__pane-right {
  min-width: 0;
}

.signin__pane-right .signin__card {
  max-width: 100%;
}

@media (width < 1024px) {
  .signin--split {
    flex-direction: column;
  }

  .signin__pane-left {
    min-height: auto;
    padding: var(--space-5);
  }

  .signin__pane-right {
    width: 100%;
    padding: var(--space-5);
  }
}
</style>
